import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from './constants';
import { authService } from './services/authService';



const TestResults = ({ onSwitchToComparative }) => {

  const timeStampConverter = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  }

  const columnHead = {
    '☑': {field : 'select', simpleRender: false, filter: false},
    'Test Id':{field : 'testInstance', simpleRender: false, filter: true},
    'Status': {field : 'analysisStatus', simpleRender: false, filter: true},
    'Time Stamp': {field: 'timeStamp', simpleRender: true, filter: true, valueConverter:timeStampConverter},
    'Host Name': {field: 'appHostName', simpleRender: true, filter: true},
    'Patient' : {field: 'patient', simpleRender: true, filter: true},
    'M/F': {field: 'gender' , simpleRender: true, filter: true},
    'Age': {field: 'age', simpleRender: true, filter: true},
    'Height (cm)': {field: 'height', simpleRender: true, filter: true},
    'Weight (kg)': {field: 'weight', simpleRender: true, filter: true},
    'Doctor': {field: 'doctor', simpleRender: true, filter: true},
    'Technician': {field: 'technician', simpleRender: true, filter: true},
    'Stages': {field: 'stages', simpleRender: true, filter: true},
    'Durations': {field: 'durations', simpleRender: true, filter: true},
    'Delay': {field: 'delay', simpleRender: true, filter: true},
    'Wave-DAC1': {field: 'DAC1Wave', simpleRender: true, filter: true},
    'Feq-DAC1': {field: 'DAC1Feq', simpleRender: true, filter: true},
    'Amp-DAC1': {field: 'DAC1Amp', simpleRender: true, filter: true},
    'Wave-DAC2': {field: 'DAC2Wave', simpleRender: true, filter: true},
    'Feq-DAC2': {field: 'DAC2Feq', simpleRender: true, filter: true},
    'Amp-DAC2': {field: 'DAC2Amp', simpleRender: true, filter: true},
    'AC Freq': {field: 'acFreq', simpleRender: true, filter: true},
    'Notes': {field: 'metaStr', simpleRender: true, filter: true},
    'PPG Sensor': {field: 'ppgSensor', simpleRender: false, filter: true},
    'ConfmA(R,IR)': {field: 'infoPPG_configured', simpleRender: false, filter: true},
    'CalibmA(R,IR)': {field: 'infoPPG_calibrated', simpleRender: false, filter: true},
    'Stage Boundaries': {field: 'stageBoundariesOverride', simpleRender: false, filter: true},
    'Reports': {field: 'analysisStatus', simpleRender: false, filter: false},
    '⬇': {field: 'testInstance', simpleRender: false, filter: false},
    '💬': {field: 'comment', simpleRender: false, filter: false},
    'Tags': {field: 'tags', simpleRender: false, filter: true},
  };

  const convertFieldToColumnHeadKey = (field) => {
    for (const [key, value] of Object.entries(columnHead)) {
      if (value.field === field) {
        return key;
      }
    }
    return field; // Return the original field if not found
  };

  const [items, setItems] = useState([]);
  const [filterText, setFilterText] = useState(''); // State for the filter text
  const [columnFilters, setColumnFilters] = useState({});
  const [popupVisible, setPopupVisible] = useState(false);
  const [currentTestInstance, setCurrentTestInstance] = useState(null);
  const [currentComment, setCurrentComment] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [availableTags, setAvailableTags] = useState([]);
  const [tagPopupVisible, setTagPopupVisible] = useState(false);
  const [currentTestForTags, setCurrentTestForTags] = useState(null);
  const [tagCreatorFilter, setTagCreatorFilter] = useState('');
  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [hasSensorPlacement, setHasSensorPlacement] = useState(false);

  // Comparative analysis state
  const [selectedTestIds, setSelectedTestIds] = useState([]);

  // Column visibility state - Initialize with all columns visible
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('testResultsVisibleColumns');
    return saved ? JSON.parse(saved) : Object.keys(columnHead).reduce((acc, col) => ({ ...acc, [col]: true }), {});
  });
  const [columnSelectorVisible, setColumnSelectorVisible] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  // Removed totalItems state - now calculated directly

  // Removed hasFetched ref since we now allow periodic refreshes

  useEffect(() => {
    // Define fetch functions
    const fetchData = () => {
      console.log('Fetching entries...');
      authService.apiFetch(`${API_BASE_URL}/entries`, {
        headers: authService.getAuthHeaders()
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          return res.json();
        })
        .then((data) => {
          console.log('Entries fetched successfully');
          setItems(data);
        })
        .catch((err) => {
          console.error('Error fetching entries:', err);
        });
    };

    const fetchTags = () => {
      console.log('Fetching tags...');
      authService.apiFetch(`${API_BASE_URL}/tags`, {
        headers: authService.getAuthHeaders()
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          return res.json();
        })
        .then((data) => {
          console.log('Tags fetched successfully');
          setAvailableTags(data);
        })
        .catch((err) => {
          console.error('Error fetching tags:', err);
        });
    };

    // Fetch immediately on mount
    console.log('Initial fetch on component mount');
    fetchData();
    fetchTags();

    // Set up 10-second refresh interval
    const intervalId = setInterval(() => {
      console.log('Refreshing data (10-second interval)...');
      fetchData();
      fetchTags();
    }, 10000);

    // Cleanup interval on unmount
    return () => {
      console.log('Cleaning up refresh interval');
      clearInterval(intervalId);
    };
  }, []); // Empty dependency array - only run once on mount

  const handleSort = (columnKey) => {
    const field = columnHead[columnKey].field;
    let direction = 'asc';
    
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key: field, direction });
  };

  const getSortedItems = (items) => {
    if (!sortConfig.key) return items;
    
    return [...items].sort((a, b) => {
      // For sorting, always use raw values (not converted values)
      // This ensures proper chronological sorting for timestamps
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // For numeric comparison (including timestamps)
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // For string comparison
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Global filter logic
      const globalFilterTerms = filterText.toLowerCase().split(' ').filter(Boolean); // Split and remove empty terms
      const globalFilterMatch = globalFilterTerms.every((term) =>
        Object.keys(item).some((key) => {
          // Special handling for tags field in global search
          if (key === 'tags') {
            const tagIds = item[key] || [];
            const tagNames = tagIds.map(tagId => {
              const tag = availableTags.find(t => t._id === tagId);
              return tag ? tag.name.toLowerCase() : '';
            }).filter(Boolean);

            return tagNames.some(tagName => tagName.includes(term));
          }

          const value = item[key]?.toString().toLowerCase() || '';
          return value.includes(term);
        })
      );

      // Column-specific filter logic
      const columnFilterMatch = Object.keys(columnFilters).every((key) => {
        if (!columnFilters[key]) return true; // Skip if no filter is applied for the column
        // Convert the field to the corresponding column head key
        // to ensure the filter is applied correctly
        let cHeadKey = convertFieldToColumnHeadKey(key);

        // Special handling for tags field
        if (key === 'tags') {
          const tagIds = item[key] || [];
          const tagNames = tagIds.map(tagId => {
            const tag = availableTags.find(t => t._id === tagId);
            return tag ? tag.name.toLowerCase() : '';
          }).filter(Boolean);

          return tagNames.some(tagName => tagName.includes(columnFilters[key].toLowerCase()));
        }

        // Special handling for PPG sensor field
        if (key === 'ppgSensor') {
          const ppgText = item.ppgSensor?.sensorPPG
            ? `${item.ppgSensor.sensorPPG.hand} ${item.ppgSensor.sensorPPG.placement} ${item.ppgSensor.sensorPPG.direction}`.toLowerCase()
            : '';
          return ppgText.includes(columnFilters[key].toLowerCase());
        }

        // Special handling for Configured Current field
        if (key === 'infoPPG_configured') {
          const configuredRed = item.infoPPG?.redLEDCurrent || 'N/A';
          const configuredIr = item.infoPPG?.irLEDCurrent || 'N/A';
          const currentText = `${configuredRed} ${configuredIr}`.toLowerCase();
          return currentText.includes(columnFilters[key].toLowerCase());
        }

        // Special handling for Calibrated Current field
        if (key === 'infoPPG_calibrated') {
          const calibratedRed = item.infoPPG?.ppgCalibData?.redLEDCurrentMA || 'N/A';
          const calibratedIr = item.infoPPG?.ppgCalibData?.irLEDCurrentMA || 'N/A';
          const currentText = `${calibratedRed} ${calibratedIr}`.toLowerCase();
          return currentText.includes(columnFilters[key].toLowerCase());
        }

        // Check if the column has a valueConverter and apply it if present
        // Otherwise, use the raw value
        let value = ((columnHead[cHeadKey].valueConverter) ?
           columnHead[cHeadKey].valueConverter(item[key]) : item[key]?.toString().toLowerCase()) || '';
        return value.includes(columnFilters[key].toLowerCase());
      });

      // Tag creator filter logic
      const tagCreatorMatch = (() => {
        if (!tagCreatorFilter) return true; // No filter applied

        const tagIds = item.tags || [];
        if (tagIds.length === 0) return false; // No tags, can't match creator

        // Check if any of the item's tags were created by the specified user
        return tagIds.some(tagId => {
          const tag = availableTags.find(t => t._id === tagId);
          return tag && tag.createdBy && tag.createdBy.toLowerCase().includes(tagCreatorFilter.toLowerCase());
        });
      })();

      // Return true if all filters match
      return globalFilterMatch && columnFilterMatch && tagCreatorMatch;
    });
  }, [items, filterText, columnFilters, availableTags, tagCreatorFilter]);

  const sortedAndFilteredItems = useMemo(() => {
    return getSortedItems(filteredItems);
  }, [filteredItems, sortConfig]);
  
  // Calculate total items directly without useEffect to prevent recursion
  const totalItemsCount = sortedAndFilteredItems.length;

  // Calculate total pages
  const calculatedTotalPages = Math.ceil(totalItemsCount / itemsPerPage);

  // Use a separate useEffect for page reset to avoid calling setState during render
  useEffect(() => {
    console.log('Page reset useEffect running:', { currentPage, calculatedTotalPages });
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      console.log(`Resetting page from ${currentPage} to 1 because totalPages is ${calculatedTotalPages}`);
      setCurrentPage(1);
    }
  }, [currentPage, calculatedTotalPages]);

  // Pagination logic
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedAndFilteredItems.slice(startIndex, endIndex);
  }, [sortedAndFilteredItems, currentPage, itemsPerPage]);

  const totalPages = calculatedTotalPages;

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show ellipsis and smart pagination
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getPdf = (testInstance) => {
    window.open(
      `${API_BASE_URL}/getPdf?testInstance=${testInstance}&token=${authService.getToken()}`,
      '_blank', 'noopener,noreferrer'
    );
  };

  const getHtmlReport = (testInstance) => {
    const a = document.createElement('a');
    a.href = `${API_BASE_URL}/web-report?testInstance=${testInstance}&token=${authService.getToken()}`;
    a.download = `${testInstance}_web_report.html.gz`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 100);
  };

  const getWebReport = (testInstance, reportType) => {
    const fileName = reportType === 'PPG' ? `${testInstance}_PPG_report.html` : `${testInstance}_ekg_report.html`;
    window.open(
      `${API_BASE_URL}/web-reports/${testInstance}/${fileName}?token=${authService.getToken()}`,
      '_blank', 'noopener,noreferrer'
    );
  };

  const downloadZip = (testInstance) => {
    const a = document.createElement('a');
    a.href = `${API_BASE_URL}/downloadZip?testInstance=${testInstance}&token=${authService.getToken()}`;
    a.download = `${testInstance}.zip`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 100);
  };

  const openCommentEditor = (testInstance, comment) => {
    setCurrentTestInstance(testInstance);
    setCurrentComment(comment || ''); // Default to empty string if no comment exists
    setPopupVisible(true);
  };

  const closeCommentEditor = () => {
    setPopupVisible(false);
    setCurrentTestInstance(null);
    setCurrentComment('');
  };

  const saveComment = async () => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/updateComment`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testInstance: currentTestInstance,
          comment: currentComment,
        }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to update comment on the server');
      }
  
      const result = await response.json();
      console.log(`Comment updated successfully for Test Instance: ${currentTestInstance}`, result);
  
      closeCommentEditor(); // Close the popup after saving
    } catch (error) {
      console.error('Error updating comment:', error);
      alert(`Failed to update comment for Test Instance: ${currentTestInstance}`);
    }
  };

  const renderCommentPopup = () => {
    if (!popupVisible) return null;
  
    return (
      <div className="popup-overlay">
        <div className="popup-content">
          <h3>Edit Comment</h3>
          <textarea
            value={currentComment}
            onChange={(e) => setCurrentComment(e.target.value)}
            placeholder="Enter comment here..."
            style={{
              width: '100%',
              height: '100px',
              padding: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ marginTop: '10px', textAlign: 'right' }}>
            <button onClick={saveComment} style={{ marginRight: '10px' }}>
              Save
            </button>
            <button onClick={closeCommentEditor}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditPopup = () => {
    if (!editPopupVisible || !editingTest) return null;

    return (
      <div className="popup-overlay">
        <div className="popup-content" style={{ maxWidth: '600px' }}>
          <h3>Edit Test Metadata - {editingTest}</h3>
          <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Note: Test Instance, Timestamp, and Hardware Parameters cannot be edited
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            {/* Patient Information */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Patient Name</label>
              <input
                type="text"
                value={editFormData.patientName || ''}
                onChange={(e) => setEditFormData({...editFormData, patientName: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Gender</label>
              <select
                value={editFormData.gender}
                onChange={(e) => setEditFormData({...editFormData, gender: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Age</label>
              <input
                type="number"
                value={editFormData.age}
                onChange={(e) => setEditFormData({...editFormData, age: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Height (cm)</label>
              <input
                type="number"
                value={editFormData.height}
                onChange={(e) => setEditFormData({...editFormData, height: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Weight (kg)</label>
              <input
                type="number"
                value={editFormData.weight}
                onChange={(e) => setEditFormData({...editFormData, weight: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Doctor</label>
              <input
                type="text"
                value={editFormData.doctor}
                onChange={(e) => setEditFormData({...editFormData, doctor: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Technician</label>
              <input
                type="text"
                value={editFormData.technician}
                onChange={(e) => setEditFormData({...editFormData, technician: e.target.value})}
                style={{ width: '100%', padding: '5px' }}
              />
            </div>
          </div>

          {/* PPG Sensor Placement Section - Only show if sensor placement exists */}
          {hasSensorPlacement && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
              <h4 style={{ marginTop: '0', marginBottom: '10px' }}>PPG Sensor Placement</h4>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <div style={{ flex: '0 0 100px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Hand</label>
                  <select
                    value={editFormData.ppgHand}
                    onChange={(e) => setEditFormData({...editFormData, ppgHand: e.target.value})}
                    style={{ width: '100%', padding: '3px', fontSize: '12px' }}
                  >
                    <option value="Left">Left</option>
                    <option value="Right">Right</option>
                  </select>
                </div>

                <div style={{ flex: '0 0 130px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Placement</label>
                  <select
                    value={editFormData.ppgPlacement}
                    onChange={(e) => setEditFormData({...editFormData, ppgPlacement: e.target.value})}
                    style={{ width: '100%', padding: '3px', fontSize: '12px' }}
                  >
                    <option value="Index Finger">Index Finger</option>
                    <option value="Wrist">Wrist</option>
                    <option value="Thumb">Thumb</option>
                  </select>
                </div>

                <div style={{ flex: '0 0 100px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Direction</label>
                  <select
                    value={editFormData.ppgDirection}
                    onChange={(e) => setEditFormData({...editFormData, ppgDirection: e.target.value})}
                    style={{ width: '100%', padding: '3px', fontSize: '12px' }}
                  >
                    <option value="Top">Top</option>
                    <option value="Bottom">Bottom</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Stage Boundaries Section */}
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '5px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: '0' }}>Stage Boundaries (seconds)</h4>
              <button
                type="button"
                onClick={() => {
                  setEditFormData({
                    ...editFormData,
                    stage_uncuff_start: '',
                    stage_uncuff_end: '',
                    stage_occlusion_start: '',
                    stage_occlusion_end: '',
                    stage_release_start_fp: '',
                    stage_release_end: '',
                    stage_release_start_raw: '',
                    resetStageBoundaries: true
                  });
                }}
                style={{
                  padding: '5px 15px',
                  fontSize: '12px',
                  backgroundColor: '#ff6b6b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset Stage Boundaries
              </button>
            </div>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Baseline Start</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_uncuff_start || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_uncuff_start: e.target.value})}
                      placeholder="e.g., 30.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Baseline End</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_uncuff_end || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_uncuff_end: e.target.value})}
                      placeholder="e.g., 50.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div></div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Occlusion Start</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_occlusion_start || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_occlusion_start: e.target.value})}
                      placeholder="e.g., 70.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Occlusion End</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_occlusion_end || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_occlusion_end: e.target.value})}
                      placeholder="e.g., 370.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div></div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Release Start Raw</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_release_start_raw || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_release_start_raw: e.target.value})}
                      placeholder="e.g., 375.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Release Start Fp</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_release_start_fp || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_release_start_fp: e.target.value})}
                      placeholder="e.g., 380.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div></div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}>Release End</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData.stage_release_end || ''}
                      onChange={(e) => setEditFormData({...editFormData, stage_release_end: e.target.value})}
                      placeholder="e.g., 620.0"
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </div>
                  <div></div>
                  <div></div>
                </div>
              </div>
              {editFormData.stage_boundaries_fp && (
                <div style={{ minWidth: '200px', padding: '10px', backgroundColor: '#dbeeff', borderRadius: '4px', borderLeft: '3px solid #4a9fd5' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '12px', color: '#2c7ab5', marginBottom: '8px' }}>Auto Detected Values</div>
                  {[
                    { label: 'Baseline', key: 'baseline' },
                    { label: 'Occlusion', key: 'occlusion' },
                    { label: 'Release', key: 'release' },
                  ].map(({ label, key }) => (
                    <div key={key} style={{ marginBottom: '8px', fontSize: '12px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{label}</div>
                      {key === 'release' ? (
                        <div style={{ color: '#444' }}>
                          StartRaw: {editFormData.stage_boundaries_raw?.[key]?.start ?? '—'} &nbsp;|&nbsp;
                          StartFp: {editFormData.stage_boundaries_fp[key]?.start ?? '—'} &nbsp;|&nbsp;
                          End: {editFormData.stage_boundaries_fp[key]?.end ?? '—'}
                        </div>
                      ) : (
                        <div style={{ color: '#444' }}>
                          Start: {editFormData.stage_boundaries_fp[key]?.start ?? '—'} &nbsp;|&nbsp;
                          End: {editFormData.stage_boundaries_fp[key]?.end ?? '—'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>


          {/* Notes - Full Width */}
          <div style={{ marginTop: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Notes</label>
            <textarea
              value={editFormData.metaStr}
              onChange={(e) => setEditFormData({...editFormData, metaStr: e.target.value})}
              style={{ width: '100%', padding: '8px', minHeight: '80px' }}
            />
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={closeEditForm}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveEditedTest}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontWeight: '500',
                boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)'
              }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTagPopup = () => {
    if (!tagPopupVisible || !currentTestForTags) return null;

    const currentTags = getCurrentTestTags(currentTestForTags);

    return (
      <div className="popup-overlay">
        <div className="popup-content" style={{ maxWidth: '600px', maxHeight: '500px', overflowY: 'auto' }}>
          <h3>Manage Tags for {currentTestForTags}</h3>
          
          <div style={{ marginBottom: '20px' }}>
            <h4>Current Tags:</h4>
            {currentTags.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No tags attached</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {currentTags.map((tagId) => {
                  const tag = availableTags.find(t => t._id === tagId);
                  if (!tag) return null;
                  return (
                    <div key={tagId} style={{
                      backgroundColor: tag.color,
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      {tag.name}
                      <button
                        onClick={() => detachTag(currentTestForTags, tagId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '0'
                        }}
                        title="Remove tag"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h4>Available Tags:</h4>
            {availableTags.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No tags available. Create tags in the Tag Management tab.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableTags.map((tag) => {
                  const isAttached = currentTags.includes(tag._id);
                  return (
                    <button
                      key={tag._id}
                      onClick={() => !isAttached && attachTag(currentTestForTags, tag._id)}
                      disabled={isAttached}
                      style={{
                        backgroundColor: isAttached ? '#f0f0f0' : tag.color,
                        color: isAttached ? '#666' : 'white',
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: isAttached ? 'not-allowed' : 'pointer',
                        opacity: isAttached ? 0.5 : 1
                      }}
                      title={isAttached ? 'Already attached' : `Attach ${tag.name}`}
                    >
                      {tag.name} {isAttached && '✓'}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button onClick={closeTagEditor}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const handleRerunAnalysis = async (testInstance) => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/rerunAnalysis?testInstance=${testInstance}`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(response.message);
      }

      const result = await response.json();
      alert(`Analysis started in background: ${result.message}`);
    } catch (error) {
      console.error('Error starting rerun analysis:', error);
      alert('Failed to start rerun analysis');
    }
  }

  const openTagEditor = (testInstance) => {
    setCurrentTestForTags(testInstance);
    setTagPopupVisible(true);
  };

  const closeTagEditor = () => {
    setTagPopupVisible(false);
    setCurrentTestForTags(null);
  };

  const attachTag = async (testInstance, tagId) => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/tags/attach/${testInstance}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tagId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to attach tag');
      }

      // Refresh data to show updated tags
      authService.apiFetch(`${API_BASE_URL}/entries`, {
        headers: authService.getAuthHeaders()
      })
        .then((res) => res.json())
        .then((data) => setItems(data))
        .catch((err) => console.error(err));

    } catch (error) {
      console.error('Error attaching tag:', error);
      alert(`Failed to attach tag: ${error.message}`);
    }
  };

  const detachTag = async (testInstance, tagId) => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/tags/detach/${testInstance}/${tagId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to detach tag');
      }

      // Refresh data to show updated tags
      authService.apiFetch(`${API_BASE_URL}/entries`, {
        headers: authService.getAuthHeaders()
      })
        .then((res) => res.json())
        .then((data) => setItems(data))
        .catch((err) => console.error(err));

    } catch (error) {
      console.error('Error detaching tag:', error);
      alert(`Failed to detach tag: ${error.message}`);
    }
  };

  const getCurrentTestTags = (testInstance) => {
    const testItem = items.find(item => item.testInstance === testInstance);
    return testItem ? testItem.tags || [] : [];
  };

  // Columns that are always visible and cannot be hidden
  const lockedColumns = ['Test Id', 'Status'];

  // Column visibility functions
  const toggleColumnVisibility = (columnName) => {
    if (lockedColumns.includes(columnName)) return;
    setVisibleColumns(prev => {
      const updated = { ...prev, [columnName]: !prev[columnName] };
      localStorage.setItem('testResultsVisibleColumns', JSON.stringify(updated));
      return updated;
    });
  };

  const showAllColumns = () => {
    const allVisible = Object.keys(columnHead).reduce((acc, col) => ({ ...acc, [col]: true }), {});
    setVisibleColumns(allVisible);
    localStorage.setItem('testResultsVisibleColumns', JSON.stringify(allVisible));
  };

  const hideAllColumns = () => {
    const allHidden = Object.keys(columnHead).reduce((acc, col) => ({
      ...acc,
      [col]: lockedColumns.includes(col) ? true : false
    }), {});
    setVisibleColumns(allHidden);
    localStorage.setItem('testResultsVisibleColumns', JSON.stringify(allHidden));
  };

  const openEditForm = (item) => {
    setEditingTest(item.testInstance);
    // Check if this test has sensor placement data
    const hasSensorData = !!(item.ppgSensor?.sensorPPG);
    setHasSensorPlacement(hasSensorData);
    console.log(`Opening edit form for ${item.testInstance}, hasSensorData:`, hasSensorData, 'ppgSensor:', item.ppgSensor);

    // Populate form with current values
    setEditFormData({
      patientName: item.patient || '',
      gender: item.gender || '',
      age: item.age || '',
      height: item.height || '',
      weight: item.weight || '',
      doctor: item.doctor || '',
      technician: item.technician || '',
      metaStr: item.metaStr || '',
      // PPG sensor placement (only if present)
      ppgHand: item.ppgSensor?.sensorPPG?.hand || 'Left',
      ppgPlacement: item.ppgSensor?.sensorPPG?.placement || 'Index Finger',
      ppgDirection: item.ppgSensor?.sensorPPG?.direction || 'Top',
      // Stage Boundaries
      stage_uncuff_start: item.stageBoundariesOverride?.baseline?.start || '',
      stage_uncuff_end: item.stageBoundariesOverride?.baseline?.end || '',
      stage_occlusion_start: item.stageBoundariesOverride?.occlusion?.start || '',
      stage_occlusion_end: item.stageBoundariesOverride?.occlusion?.end || '',
      stage_release_start_fp: item.stageBoundariesOverride?.release?.start_fp || '',
      stage_release_end: item.stageBoundariesOverride?.release?.end || '',
      stage_release_start_raw: item.stageBoundariesOverride?.release?.start_raw || '',
      stage_boundaries_fp: item.stage_boundaries_fp || null,
      stage_boundaries_raw: item.stage_boundaries_raw || null,
    });
    setEditPopupVisible(true);
  };

  const closeEditForm = () => {
    setEditPopupVisible(false);
    setEditingTest(null);
    setEditFormData({});
  };

  const saveEditedTest = async () => {
    try {
      // Prepare metadata with HI metrics transformed into the correct format
      const metadata = { ...editFormData };

      // Check if reset Stage Boundaries was requested
      const shouldResetStageBoundaries = metadata.resetStageBoundaries;

      // Transform HI metrics into nested structure if any values are provided
      const hasStageBoundaries = metadata.stage_uncuff_start || metadata.stage_uncuff_end ||
                           metadata.stage_occlusion_start || metadata.stage_occlusion_end ||
                           metadata.stage_release_start_fp || metadata.stage_release_end ||
                           metadata.stage_release_start_raw;

      if (shouldResetStageBoundaries) {
        // Signal to backend to delete stageBoundariesOverride
        metadata.stageBoundariesOverride = null;
      } else if (hasStageBoundaries) {
        // Parse values to float with 1 decimal precision
        const parseFloat1Dec = (val) => val ? parseFloat(parseFloat(val).toFixed(1)) : undefined;

        metadata.stageBoundariesOverride = {
          baseline: {
            start: parseFloat1Dec(metadata.stage_uncuff_start),
            end: parseFloat1Dec(metadata.stage_uncuff_end),
          },
          occlusion: {
            start: parseFloat1Dec(metadata.stage_occlusion_start),
            end: parseFloat1Dec(metadata.stage_occlusion_end),
          },
          release: {
            start_fp: parseFloat1Dec(metadata.stage_release_start_fp),
            end: parseFloat1Dec(metadata.stage_release_end),
            start_raw: parseFloat1Dec(metadata.stage_release_start_raw),
          }
        };
      }

      // Remove the flat HI metrics fields from metadata
      delete metadata.stage_uncuff_start;
      delete metadata.stage_uncuff_end;
      delete metadata.stage_occlusion_start;
      delete metadata.stage_occlusion_end;
      delete metadata.stage_release_start_fp;
      delete metadata.stage_release_end;
      delete metadata.stage_release_start_raw;
      delete metadata.resetStageBoundaries;

      const response = await authService.apiFetch(`${API_BASE_URL}/updateTestMetadata`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testInstance: editingTest,
          metadata: metadata,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update test metadata');
      }

      const result = await response.json();
      console.log('Test metadata updated successfully:', result);

      // Refresh data to show updated values
      authService.apiFetch(`${API_BASE_URL}/entries`, {
        headers: authService.getAuthHeaders()
      })
        .then((res) => res.json())
        .then((data) => setItems(data))
        .catch((err) => console.error(err));

      closeEditForm();
      alert('Test metadata updated successfully!');
    } catch (error) {
      console.error('Error updating test metadata:', error);
      alert(`Failed to update test metadata: ${error.message}`);
    }
  };

  const handleRowSelect = (testInstance) => {
    setSelectedTestIds(prev => {
      if (prev.includes(testInstance)) {
        return prev.filter(id => id !== testInstance);
      } else {
        return [...prev, testInstance];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTestIds.length === filteredItems.length) {
      setSelectedTestIds([]);
    } else {
      setSelectedTestIds(filteredItems.map(item => item.testInstance));
    }
  };

  const renderTableCells = (item, columns) => {
    return Object.keys(columns)
      .filter(key => visibleColumns[key])
      .map((key) => {
      // Skip columns that are rendered separately in the table body
      if (key === '☑' || key === 'Test Id' || key === 'Status' ||
          key === 'Reports' || key === '⬇' || key === '💬' || key === 'Tags') {
        return null;
      }

      // Handle PPG Sensor custom rendering (placement only)
      if (key === 'PPG Sensor') {
        const sensorInfo = item.ppgSensor?.sensorPPG
          ? `${item.ppgSensor.sensorPPG.hand} ${item.ppgSensor.sensorPPG.placement}, ${item.ppgSensor.sensorPPG.direction}`
          : 'N/A';

        return (
          <td key="ppgSensor" style={{ fontSize: '11px', maxWidth: '150px' }}>
            {sensorInfo}
          </td>
        );
      }

      // Handle Configured Current column
      if (key === 'ConfmA(R,IR)') {
        const configuredRed = item.infoPPG?.redLEDCurrent;
        const configuredIr = item.infoPPG?.irLEDCurrent;

        return (
          <td key="infoPPG_configured" style={{ fontSize: '11px', maxWidth: '100px' }}>
            ({configuredRed || 'N/A'}, {configuredIr || 'N/A'})
          </td>
        );
      }

      // Handle Calibrated Current column
      if (key === 'CalibmA(R,IR)') {
        const calibratedRed = item.infoPPG?.ppgCalibData?.redLEDCurrentMA;
        const calibratedIr = item.infoPPG?.ppgCalibData?.irLEDCurrentMA;

        return (
          <td key="infoPPG_calibrated" style={{ fontSize: '11px', maxWidth: '100px' }}>
            ({calibratedRed || 'N/A'}, {calibratedIr || 'N/A'})
          </td>
        );
      }

      // Handle Stage Boundaries column
      if (key === 'Stage Boundaries') {
        const stageBoundaries = item.stageBoundariesOverride;
        let displayText = 'N/A';

        if (stageBoundaries) {
          const baseline = stageBoundaries.baseline ? `[${stageBoundaries.baseline.start || '?'},${stageBoundaries.baseline.end || '?'}]` : null;
          const occlusion = stageBoundaries.occlusion ? `[${stageBoundaries.occlusion.start || '?'},${stageBoundaries.occlusion.end || '?'}]` : null;
          const release = stageBoundaries.release ? `[${stageBoundaries.release.start_raw || '?'},${stageBoundaries.release.start_fp || '?'},${stageBoundaries.release.end || '?'}]` : null;

          const parts = [baseline, occlusion, release].filter(Boolean);
          if (parts.length > 0) {
            displayText = `[${parts.join(',')}]`;
          }
        }

        return (
          <td key="stageBoundariesOverride" style={{ fontSize: '11px', maxWidth: '180px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
            {displayText}
          </td>
        );
      }

      if (!columns[key].simpleRender) return null; // Skip if simpleRender is false

      // Check if valueConverter is present and use it to evaluate the value
      const value = columns[key].valueConverter
        ? columns[key].valueConverter(item[columns[key].field]) // Use valueConverter if defined
        : item[columns[key].field]; // Otherwise, use the raw value

      return <td key={columns[key].field}>{value}</td>;
    }).filter((td) => td !== null); // Filter out null values
  };

  const renderTableHeaderWithFilter = (columns) => {
    return Object.keys(columns)
      .filter(column => visibleColumns[column])
      .map((column) => (
      <th key={column} style={{ padding: '8px', border: '1px solid black' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {column === '☑' ? (
            <input
              type="checkbox"
              checked={selectedTestIds.length === filteredItems.length && filteredItems.length > 0}
              onChange={handleSelectAll}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              title="Select all"
            />
          ) : (
            <span
              onClick={() => handleSort(column)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort"
            >
              {column}
              {sortConfig.key === columns[column].field && (
                <span style={{ marginLeft: '5px' }}>
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </span>
          )}
        </div>
        {columns[column].filter && columns[column].field === 'analysisStatus' ? (
          // Render a dropdown for the "analysisStatus" column
          <select
            className="filter-column-input"
            onChange={(e) =>
              setColumnFilters((prevFilters) => ({
                ...prevFilters,
                [columns[column].field]: e.target.value, // Dynamically update the filter for the specific column
              }))
            }
          >
            <option className="filter-column-input" value="">All</option>
            <option className="filter-column-input" value="Succeeded">✅ Succeeded</option>
            <option className="filter-column-input" value="Pending">⏳ Pending</option>
            <option className="filter-column-input" value="Failed">❌ Failed</option>
          </select>
        ) : (
          // Render a text input for other columns
          columns[column].filter && (
            <input
              className="filter-column-input"
              type="text"
              placeholder={`Filter ${column}`}
              onChange={(e) =>
                setColumnFilters((prevFilters) => ({
                  ...prevFilters,
                  [columns[column].field]: e.target.value, // Dynamically update the filter for the specific column
                }))
              }
            />

          )
        )}
      </th>
    ));
  };
  return (
    <div className="p">
      <h1>Test Results</h1>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
        <input
          className="filter-input"
          type="text"
          placeholder="(separate terms with spaces)..."
          style={{
            padding: '2px 4px',
            lineHeight: '1.5',
            flex: '0.7'
          }}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
            Tag Creator:
          </label>
          <select
            value={tagCreatorFilter}
            onChange={(e) => setTagCreatorFilter(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          >
            <option value="">All Creators</option>
            {[...new Set(availableTags.map(tag => tag.createdBy).filter(Boolean))]
              .sort()
              .map(creator => (
                <option key={creator} value={creator}>
                  {creator}
                </option>
              ))
            }
          </select>
        </div>
        <button
          onClick={() => setColumnSelectorVisible(!columnSelectorVisible)}
          style={{
            padding: '6px 12px',
            backgroundColor: columnSelectorVisible ? '#2563eb' : '#f8f9fa',
            color: columnSelectorVisible ? 'white' : '#495057',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          title="Show/hide table columns"
        >
          <span>⚙️</span>
          <span>Columns</span>
        </button>
      </div>

      {/* Column Selector Panel */}
      {columnSelectorVisible && (
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#495057' }}>Column Visibility</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={showAllColumns}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Show All
              </button>
              <button
                onClick={hideAllColumns}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Hide All
              </button>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px'
          }}>
            {Object.keys(columnHead).map(columnName => {
              const isLocked = lockedColumns.includes(columnName);
              return (
              <label
                key={columnName}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 10px',
                  backgroundColor: isLocked ? '#f1f5f9' : 'white',
                  border: `1px solid ${isLocked ? '#cbd5e1' : '#e2e8f0'}`,
                  borderRadius: '4px',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <input
                  type="checkbox"
                  checked={visibleColumns[columnName]}
                  onChange={() => toggleColumnVisibility(columnName)}
                  disabled={isLocked}
                  style={{ marginRight: '8px', cursor: isLocked ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ color: '#1e293b', flex: 1 }}>
                  {columnName}
                </span>
                {isLocked && (
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px' }} title="This column cannot be hidden">🔒</span>
                )}
              </label>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Summary Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
        margin: '20px 0',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
            {items.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Total Tests
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
            {items.filter(item => item.analysisStatus === 'Succeeded').length}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Completed
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
            {items.filter(item => item.analysisStatus === 'Pending').length}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Pending
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
            {items.filter(item => item.analysisStatus === 'Failed').length}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Failed
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
            {new Set(items.map(item => item.patient)).size}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Unique Patients
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#20c997' }}>
            {availableTags.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Available Tags
          </div>
        </div>
      </div>
      
      {/* Pagination Info and Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        margin: '10px 0',
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Showing {paginatedItems.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * itemsPerPage, totalItemsCount)} of {totalItemsCount} results
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 10px',
              border: '1px solid #ccc',
              backgroundColor: currentPage === 1 ? '#f0f0f0' : '#fff',
              color: currentPage === 1 ? '#999' : '#333',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            First
          </button>

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 10px',
              border: '1px solid #ccc',
              backgroundColor: currentPage === 1 ? '#f0f0f0' : '#fff',
              color: currentPage === 1 ? '#999' : '#333',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            Previous
          </button>
          
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={page === '...'}
              style={{
                padding: '6px 10px',
                border: '1px solid #ccc',
                backgroundColor: page === currentPage ? '#007bff' : '#fff',
                color: page === currentPage ? '#fff' : '#000',
                cursor: page === '...' ? 'default' : 'pointer',
                borderRadius: '4px',
                fontSize: '12px',
                minWidth: '32px'
              }}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 10px',
              border: '1px solid #ccc',
              backgroundColor: currentPage === totalPages ? '#f0f0f0' : '#fff',
              color: currentPage === totalPages ? '#999' : '#333',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            Next
          </button>

          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 10px',
              border: '1px solid #ccc',
              backgroundColor: currentPage === totalPages ? '#f0f0f0' : '#fff',
              color: currentPage === totalPages ? '#999' : '#333',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            Last
          </button>
        </div>
      </div>

      {/* Comparative Analysis Button */}
      {selectedTestIds.length > 0 && (
        <div style={{
          margin: '10px 0',
          padding: '10px',
          backgroundColor: selectedTestIds.length < 2 ? '#fff3cd' : '#e3f2fd',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontWeight: 'bold' }}>
            {selectedTestIds.length} test{selectedTestIds.length > 1 ? 's' : ''} selected
            {selectedTestIds.length < 2 && <span style={{ color: '#856404', marginLeft: '10px' }}>(Select at least 2 tests for comparison)</span>}
          </span>
          <button
            onClick={() => {
              if (selectedTestIds.length < 2) {
                alert('Please select at least 2 tests for comparative analysis.');
                return;
              }
              // Store selected test IDs in localStorage and switch to Comparative Analysis tab
              localStorage.setItem('selectedTestIds', JSON.stringify(selectedTestIds));
              if (onSwitchToComparative) {
                onSwitchToComparative();
              }
            }}
            disabled={selectedTestIds.length < 2}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedTestIds.length < 2 ? '#6c757d' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedTestIds.length < 2 ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              opacity: selectedTestIds.length < 2 ? 0.6 : 1
            }}
          >
            📊 Compare Selected Tests
          </button>
          <button
            onClick={() => setSelectedTestIds([])}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {renderTableHeaderWithFilter(columnHead)}
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((item, index) => (
              <tr
                key={item.testInstance}
                className={index % 2 === 0 ? 'bg-gray-100' : 'bg-white'} // Alternate row shading
              >
              {visibleColumns['☑'] && (
              <td style={{ textAlign: 'center', width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedTestIds.includes(item.testInstance)}
                  onChange={() => handleRowSelect(item.testInstance)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </td>
              )}
              {visibleColumns['Test Id'] && (
              <td>
                <span
                  onClick={() => openEditForm(item)}
                  style={{
                    cursor: 'pointer',
                    color: '#007bff',
                    textDecoration: 'underline'
                  }}
                  title="Click to edit test metadata"
                >
                  {item.testInstance}
                </span>
              </td>
              )}
              {visibleColumns['Status'] && (
              <td>
                {item.analysisStatus === 'Pending' ? '⏳' :
                 item.analysisStatus === 'Succeeded' ? '✅' :
                 item.analysisStatus === 'Failed' ? '❌' : '❓'}
              </td>
              )}
              {renderTableCells(item, columnHead)}
              {visibleColumns['Reports'] && (
              <td>
                {item.analysisStatus !== 'Pending' && (
                  <>
                    <span
                      onClick={() => getPdf(item.testInstance)}
                      style={{ cursor: 'pointer', fontSize: '16px', color: '#007bff', marginRight: '8px' }}
                      title="Download PDF"
                    >
                      📄
                    </span>
                    <span
                      onClick={() => getHtmlReport(item.testInstance)}
                      style={{ cursor: 'pointer', fontSize: '16px', color: '#28a745', marginRight: '8px' }}
                      title="View HTML Report"
                    >
                      🌐
                    </span>
                    <span
                      onClick={() => getWebReport(item.testInstance, 'PPG')}
                      style={{ cursor: 'pointer', fontSize: '16px', color: '#ff6b6b', marginRight: '8px' }}
                      title="View PPG Report"
                    >
                      📈
                    </span>
                    <span
                      onClick={() => getWebReport(item.testInstance, 'EKG')}
                      style={{ cursor: 'pointer', fontSize: '16px', color: '#6c5ce7', marginRight: '8px' }}
                      title="View EKG Report"
                    >
                      💓
                    </span>
                  </>
                )}
                <span
                  onClick={() => handleRerunAnalysis(item.testInstance)}
                  title="Recreate PDF"
                  style={{ cursor: 'pointer', fontSize: '18px', color: 'blue' }}
                >
                  🔄
                </span>
              </td>
              )}
              {visibleColumns['⬇'] && (
              <td>
                <span
                  onClick={() => downloadZip(item.testInstance)}
                  title="Download ZIP (includes .dat, _desc.json, _PPG.dat, _Temp.dat)"
                  style={{ cursor: 'pointer', fontSize: '20px', color: 'blue', fontWeight: 'bold' }}
                >
                  📦
                </span>
              </td>
              )}
              {visibleColumns['💬'] && (
              <td>
                <button
                  onClick={() => openCommentEditor(item.testInstance, item.comment)} // Open editor for comment
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#007bff',
                    background: 'none',
                    border: 'none',
                    textDecoration: 'underline',
                    textAlign: 'left'
                  }}
                >
                  {item.comment? item.comment: '✏️'}
                </button>
              </td>
              )}
              {visibleColumns['Tags'] && (
              <td>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                  {(item.tags || []).map((tagId) => {
                    const tag = availableTags.find(t => t._id === tagId);
                    if (!tag) return null;
                    return (
                      <span key={tagId} style={{
                        backgroundColor: tag.color,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        {tag.name}
                      </span>
                    );
                  })}
                  <button
                    onClick={() => openTagEditor(item.testInstance)}
                    style={{
                      background: 'none',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '2px 4px',
                      color: '#666'
                    }}
                    title="Manage tags"
                  >
                    🏷️
                  </button>
                </div>
              </td>
              )}
            </tr>
          ))}
        </tbody>
        </table>
      </div>

      {/* Bottom Pagination Summary */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          margin: '20px 0',
          fontSize: '14px',
          color: '#666'
        }}>
          <span>
            Page {currentPage} of {totalPages} ({totalItemsCount} total results)
          </span>
        </div>
      )}
      
      <div>
        {renderCommentPopup()}
        {renderTagPopup()}
        {renderEditPopup()}
      </div>
    </div>
  );
}

export default TestResults;
